import React, { useState } from "react";
import clsx from "clsx";
import { getTailwindColorClass, Intent } from "lib/common/enums";
import { ErrorIcon, EyeIcon, InfoIcon } from "../Icon";

export interface TextAreaProps
  extends React.DetailedHTMLProps<
    React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    HTMLTextAreaElement
  > {
  label?: string;
  placeholder?: string;
  required?: boolean;
  labelClass?: string;
  intent?: Intent;
  customIntent?: Intent;
  customMessage?: string;
  message?: string;
  inputStyle?: string;
  row?: number;
  col?: number;
  infoIcon?: boolean;
  stateChange?: (e: any) => void;
  defaultValue?: string;
}

// eslint-disable-next-line react/display-name
const TextArea = React.forwardRef(
  (
    {
      value,
      onChange,
      label,
      placeholder,
      className,
      required = false,
      labelClass,
      intent,
      message,
      inputStyle,
      stateChange,
      customIntent,
      customMessage,
      defaultValue,
      row = 5,
      col = 8,
      infoIcon = false,
      ...rest
    }: TextAreaProps,
    ref: React.ForwardedRef<HTMLTextAreaElement>
  ) => {
    const [focus, setFocus] = useState(false);

    const [showPassword, setShowPassword] = useState(false);

    const borderColor = customIntent
      ? getTailwindColorClass(customIntent)
      : intent && getTailwindColorClass(intent);

    return (
      <>
        <label
          className={clsx(
            "flex flex-col text-md text-gray-900 font-bold",
            labelClass
          )}
        >
          {label && label.length > 0 && (
            <span className="mb-1 font-medium">
              {label}
              {required && <span className="text-red-600 ml-1">*</span>}
              {infoIcon && (
                <InfoIcon
                  className="ml-1 inline-block"
                  size={4}
                  viewBox="0 0 16 16"
                />
              )}
            </span>
          )}
          <div
            className={clsx(
              `border px-2 border-${
                !className?.includes("border-") && borderColor === "gray"
                  ? "gray-400"
                  : borderColor
              } p-2 text-gray-700 text-sm transition-all flex flex-row items-center`,
              className
            )}
          >
            <textarea
              ref={ref}
              value={value}
              onChange={(e) => {
                onChange && onChange(e);
                stateChange && stateChange(e);
              }}
              rows={row}
              cols={col}
              placeholder={focus ? "" : placeholder}
              onFocus={() => setFocus(true)}
              onBlur={() => setFocus(false)}
              required={required}
              className={clsx(
                `outline-none  align-middle w-full border-none bg-transparent focus:${borderColor}`,
                inputStyle
              )}
              {...rest}
            />
          </div>

          <span className={`font-normal text-sm text-${borderColor}`}>
            {message && message}
            {customMessage && customMessage}
          </span>
        </label>
      </>
    );
  }
);

export default TextArea;
